import { type DicomMessage, DicomMessageLevel, type DicomSeries } from '@/lib/dicom/DicomSeries';
import {
    type ApiCaseStudyCreateData,
    type CaseScanSeriesMessage,
    createStudy,
} from '@/views/case-settings/api/createStudy';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import type { DicomValidatedResultOutput } from '@/lib/dicom/validateDicoms';

export async function createCtStudy(
    fileName: string,
    caseId: number,
    toUpload: DicomValidatedResultOutput,
    webComponentVersion: string,
): Promise<string> {
    const studyBody = _createStudyData(fileName, toUpload.id, toUpload.series, webComponentVersion);
    return await createStudy(caseId, studyBody);
}

function _createStudyData(
    name: string,
    seriesId: string,
    series: DicomSeries,
    webComponentVersion: string,
): ApiCaseStudyCreateData {
    const makeFileMessage = (info: DicomInfo) => {
        return {
            name: info.file.name || '',
            messages: info.messages?.map(_makeMessage),
        };
    };

    return {
        name: name,
        source_modality: 'ct-scan',
        series: seriesId,
        file_count: series.items.length,
        messages: series.messages.map(_makeMessage),
        excluded_files: series.items?.filter((info) => info.isExcluded).map(makeFileMessage),
        file_messages: series.items?.filter((info) => !info.isExcluded).map(makeFileMessage),
        web_component_version: webComponentVersion,
    };
}

const _mapping = {
    [DicomMessageLevel.Error]: 'error',
    [DicomMessageLevel.Warning]: 'warn',
    [DicomMessageLevel.Info]: 'info',
    [DicomMessageLevel.Diagnostic]: 'debug',
};

/**
 * Make a message in wire format (with enum as a string)
 */
function _makeMessage(message: DicomMessage): CaseScanSeriesMessage {
    function _messageToLevel(v: DicomMessageLevel): string {
        return v in _mapping ? _mapping[v] : 'error';
    }

    return {
        level: _messageToLevel(message.level),
        message: message.message,
    };
}

